// BLE transport for thermal label printers.
//
// Two runtime paths:
//   • Capacitor native (Android/iOS): uses @capacitor-community/bluetooth-le
//     → active BLE scan, returns list of found devices.
//   • PWA / browser: uses Web Bluetooth requestDevice()
//     → opens the OS device picker, returns the chosen device.
//
// Supported BLE profiles:
//   1. 0xFF00 family (Clabel, Xprinter, HPRT, most Chinese thermal printers)
//   2. Nordic UART Service (NUS)
//   3. Microchip BM70

import { Capacitor } from '@capacitor/core';
import { BleClient, ConnectionPriority, type ScanResult } from '@capacitor-community/bluetooth-le';
import { serializePrinterWrite } from './serializedWriteQueue.js';

const SVC_FF00 = '0000ff00-0000-1000-8000-00805f9b34fb';
const CHR_FF02 = '0000ff02-0000-1000-8000-00805f9b34fb';

const SVC_NUS = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const CHR_NUS_TX = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';

const SVC_BM70 = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
const CHR_BM70_WRITE = '49535343-8841-43f4-a8d4-ecbe34729bb3';

// T-082: Niimbot custom service (confermato dal diagnostic su B1-HC15120554).
// Char "tuttofare" bef8d6c9 ha W|WnR|R|N — write+notify sullo stesso UUID.
// Senza questo, il fallback selezionava il BM70 service che la Niimbot espone
// per firmware update (non per stampa) → comandi ignorati.
const SVC_NIIMBOT = 'e7810a71-73ae-499d-8c15-faa9aef0c3f2';
const CHR_NIIMBOT_RW = 'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f';

const SCAN_SERVICE_UUIDS = [SVC_FF00, SVC_NUS, SVC_BM70, SVC_NIIMBOT];
const CHUNK_SIZE = 128;

// Web Bluetooth flow-control (PWA only).
// `writeWithoutResponse` has NO backpressure: a large raster job (a TSPL
// `BITMAP` label ≈ 12 KB ≈ 95 chunks) is queued far faster than the printer's
// tiny BLE buffer can drain. A SINGLE dropped chunk inside the raster shifts
// every row after it → the illegible "striped" label (LeBoccerie 2026-06-26).
// The previous "ACK every 4th chunk" pacing was not enough: the 3 un-ACKed
// writes between pace points still overflowed the buffer and dropped chunks.
// Fix: ACK EVERY chunk with write-with-response when the characteristic
// supports it — the ACK blocks until the printer has actually stored the bytes,
// so nothing is ever dropped (slower but correct; ~2-3 s for a full label).
// Only when there is no ACK channel do we fall back to writeWithoutResponse + a
// per-chunk drain delay. On native this never bites (Android negotiates a large
// MTU + HIGH priority via nativeWrite).
const WEB_PACE_MS = 18;
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export interface BlePrinterDevice {
  deviceId: string;
  name: string;
  rssi: number | null;
}

// T-081: identificazione stampanti Niimbot dal nome BLE advertised. Niimbot
// USA un protocollo binario proprietario (non TSPL) e ad oggi NON ha un
// driver in TrackEAT (vedi T-082 follow-up). Riconoscerle PRIMA del save
// evita l'errore generico al pair (oggi forziamo `classicConnect` SPP e
// `driver:'tspl'` hardcoded — entrambi falliscono o stampano vuoto).
//
// Pattern nome dispositivo Niimbot (osservati su B1, B21, B3S, B18, D11,
// D110, T8, B203, B32, ecc.): prefisso modello + dash/spazio + suffisso
// alfanumerico. Es: "B1-A1B2", "B21-D3E4", "D110-001A", "B3S-XXXX",
// "B1-HC15120554" (vendor code HC + serial). Alcuni firmware mettono
// "NIIMBOT" esplicito.
// NOTA: il suffisso può contenere QUALSIASI lettera, non solo [0-9A-F]
// (HC, JC, MC, ecc.) — vendor code + serial number. Bug precedente:
// usavamo [0-9A-F] che rifiutava "HC" (H non è hex) → il filtro non
// riconosceva il B1 dell'utente "B1-HC15120554".
const NIIMBOT_NAME_REGEX = /\b(niimbot|niimprint|(?:B1|B3|B3S|B18|B20|B21|B203|B32|D11|D101|D110|T8)[\s_-]?[A-Z0-9]{2,})\b/i;

export function isNiimbotDevice(name: string | null | undefined): boolean {
  if (!name) return false;
  return NIIMBOT_NAME_REGEX.test(name);
}

// --- Capacitor native session store ---
interface NativeConn {
  deviceId: string;
  serviceUuid: string;
  writeCharUuid: string;
  notifyCharUuid?: string;  // detected dinamicamente
}
const nativeConns = new Map<string, NativeConn>();
let nativeInitialised = false;

// T-082: MTU negoziato dopo la connect. Esposto per il diagnostic toast.
const lastMtuNegotiated = new Map<string, number>();
export function bleGetMtu(deviceId: string): number | undefined {
  return lastMtuNegotiated.get(deviceId);
}

async function ensureNativeInit(): Promise<void> {
  if (nativeInitialised) return;
  await BleClient.initialize({ androidNeverForLocation: false });
  nativeInitialised = true;
}

// --- Web Bluetooth session store ---
interface WebConn {
  device: BluetoothDevice;
  char: BluetoothRemoteGATTCharacteristic;
}
const webConns = new Map<string, WebConn>();
// Keeps BluetoothDevice objects returned by requestDevice so bleConnect can reuse them.
const webDeviceCache = new Map<string, BluetoothDevice>();

// ─────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────

/**
 * Scan for nearby BLE printers.
 * - Native: active scan for ~timeoutMs ms, returns all found devices.
 * - Web: opens OS Bluetooth picker (user selects one), returns that device.
 */
export async function bleScanPrinters(timeoutMs = 8_000, opts?: { allServices?: boolean }): Promise<BlePrinterDevice[]> {
  if (Capacitor.isNativePlatform()) {
    return nativeScan(timeoutMs, opts);
  }
  return webPick();
}

/** Connect and prepare the write channel. */
export async function bleConnect(deviceId: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    return nativeConnect(deviceId);
  }
  return webConnect(deviceId);
}

/** Send raw bytes, split into MTU-sized chunks. */
export async function bleWriteBytes(deviceId: string, data: Uint8Array): Promise<void> {
  return serializePrinterWrite(`ble:${deviceId}`, async () => {
    if (Capacitor.isNativePlatform()) {
      await nativeWrite(deviceId, data);
      return;
    }
    await webWrite(deviceId, data);
  });
}

export async function bleDisconnect(deviceId: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    nativeConns.delete(deviceId);
    try { await BleClient.disconnect(deviceId); } catch { /* ignore */ }
  } else {
    const conn = webConns.get(deviceId);
    webConns.delete(deviceId);
    try { conn?.device.gatt?.disconnect(); } catch { /* ignore */ }
  }
}

// ─────────────────────────────────────────────
// Capacitor native implementation
// ─────────────────────────────────────────────

async function nativeScan(timeoutMs: number, opts?: { allServices?: boolean }): Promise<BlePrinterDevice[]> {
  await ensureNativeInit();
  const found = new Map<string, BlePrinterDevice>();
  // T-081 follow-up: di default filtriamo per i 3 service UUID delle
  // stampanti termiche TSPL note (FF00/NUS/BM70). Con `allServices: true`
  // scansioniamo TUTTI i device BLE vicini — utile per stampanti che
  // non advertiscono i service standard nell'advertisement packet
  // (es. Niimbot: il service compare solo dopo connessione).
  // Filtro sul nome: nascondiamo i device senza name (di solito beacon
  // anonimi, AirPods, smartwatch sconosciuti — non sono stampanti).
  const scanOpts = opts?.allServices
    ? { allowDuplicates: false }
    : { services: SCAN_SERVICE_UUIDS, allowDuplicates: false };
  await BleClient.requestLEScan(
    scanOpts,
    (result: ScanResult) => {
      const id = result.device?.deviceId;
      if (!id) return;
      const name = result.localName ?? result.device?.name ?? null;
      // In modalità "all services" mostriamo solo device con nome
      // riconoscibile per ridurre rumore (AirPods, beacon, ecc.).
      if (opts?.allServices && !name) return;
      found.set(id, { deviceId: id, name: name ?? id, rssi: result.rssi ?? null });
    },
  );
  await new Promise<void>((r) => setTimeout(r, timeoutMs));
  await BleClient.stopLEScan();
  return Array.from(found.values()).sort((a, b) => (b.rssi ?? -999) - (a.rssi ?? -999));
}

// T-082: stash dei service+char enumerati dopo connect, esposto per
// diagnostica/toast.
interface BleDiagnostic {
  services: Array<{
    uuid: string;
    chars: Array<{ uuid: string; write: boolean; writeNoResp: boolean; notify: boolean; read: boolean }>;
  }>;
  chosenService: string;
  chosenWriteChar: string;
}
const lastDiagnostic = new Map<string, BleDiagnostic>();
export function bleGetDiagnostic(deviceId: string): BleDiagnostic | undefined {
  return lastDiagnostic.get(deviceId);
}

// Standard BLE services che NON sono utilizzabili come transport stampante
const STANDARD_BLE_SERVICES = new Set([
  '00001800-0000-1000-8000-00805f9b34fb', // Generic Access
  '00001801-0000-1000-8000-00805f9b34fb', // Generic Attribute
  '0000180a-0000-1000-8000-00805f9b34fb', // Device Information
  '0000180f-0000-1000-8000-00805f9b34fb', // Battery Service
]);

async function nativeConnect(deviceId: string): Promise<void> {
  if (nativeConns.has(deviceId)) return;
  await ensureNativeInit();
  await BleClient.connect(deviceId);
  // T-082: connection priority HIGH per massimizzare throughput (Android only).
  // Senza questo Android resta in BALANCED → write rate ~3-5 pkt/s.
  try {
    await BleClient.requestConnectionPriority(deviceId, ConnectionPriority.CONNECTION_PRIORITY_HIGH);
  } catch { /* ignora se non supportato */ }
  // T-082 CRITICAL: leggi MTU negoziato. Android default = 23 byte. I
  // pacchetti bitmap Niimbot sono ~60 byte → con MTU 23 vengono spezzati
  // e il firmware li scarta silenziosamente. Salviamo per il diagnostic
  // e per limitare CHUNK_SIZE.
  try {
    const mtu = await BleClient.getMtu(deviceId);
    lastMtuNegotiated.set(deviceId, mtu);
  } catch {
    lastMtuNegotiated.set(deviceId, 23);
  }
  const services = await BleClient.getServices(deviceId);
  const svcIds = services.map((s) => s.uuid.toLowerCase());

  // Salva diagnostic completo
  const diag: BleDiagnostic = {
    services: services.map((s) => ({
      uuid: s.uuid,
      chars: s.characteristics.map((c) => ({
        uuid: c.uuid,
        write: !!c.properties.write,
        writeNoResp: !!c.properties.writeWithoutResponse,
        notify: !!c.properties.notify,
        read: !!c.properties.read,
      })),
    })),
    chosenService: '',
    chosenWriteChar: '',
  };

  let serviceUuid: string;
  let writeCharUuid: string;
  // T-082: Niimbot deve venire PRIMA di BM70 — molte Niimbot espongono
  // ENTRAMBI i service (BM70 per fw update, Niimbot custom per stampa).
  // Se prendiamo BM70 i comandi di stampa sono ignorati silenziosamente.
  if (svcIds.includes(SVC_NIIMBOT)) {
    serviceUuid = SVC_NIIMBOT; writeCharUuid = CHR_NIIMBOT_RW;
  } else if (svcIds.includes(SVC_FF00)) {
    serviceUuid = SVC_FF00; writeCharUuid = CHR_FF02;
  } else if (svcIds.includes(SVC_NUS)) {
    serviceUuid = SVC_NUS; writeCharUuid = CHR_NUS_TX;
  } else if (svcIds.includes(SVC_BM70)) {
    serviceUuid = SVC_BM70; writeCharUuid = CHR_BM70_WRITE;
  } else {
    // Fallback: skip standard BLE services, scegli il primo custom con
    // una caratteristica writable. Critico per Niimbot e altre stampanti
    // che usano UUID proprietari non in whitelist.
    const candidate = services.find(
      (s) => !STANDARD_BLE_SERVICES.has(s.uuid.toLowerCase()) &&
             s.characteristics.some((c) => c.properties.write || c.properties.writeWithoutResponse),
    );
    if (!candidate) {
      diag.chosenService = '(no_writable_service)';
      lastDiagnostic.set(deviceId, diag);
      throw new Error(`ble_no_write_char — servizi: ${svcIds.join(', ')}`);
    }
    const writable = candidate.characteristics.find(
      (c) => c.properties.write || c.properties.writeWithoutResponse,
    )!;
    serviceUuid = candidate.uuid;
    writeCharUuid = writable.uuid;
  }
  diag.chosenService = serviceUuid;
  diag.chosenWriteChar = writeCharUuid;
  lastDiagnostic.set(deviceId, diag);

  // Trova la NOTIFY char nello stesso service (per Niimbot acks/replies)
  const svc = services.find((s) => s.uuid.toLowerCase() === serviceUuid.toLowerCase());
  const notifyChar = svc?.characteristics.find((c) => c.properties.notify);

  nativeConns.set(deviceId, {
    deviceId, serviceUuid, writeCharUuid,
    notifyCharUuid: notifyChar?.uuid,
  });
}

async function nativeWrite(deviceId: string, data: Uint8Array): Promise<void> {
  const conn = nativeConns.get(deviceId);
  if (!conn) throw new Error('ble_not_connected');
  for (let off = 0; off < data.length; off += CHUNK_SIZE) {
    const chunk = data.subarray(off, off + CHUNK_SIZE);
    await BleClient.writeWithoutResponse(
      conn.deviceId, conn.serviceUuid, conn.writeCharUuid,
      new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength),
    );
  }
}

// T-082: writeWithResponse per protocolli che richiedono ACK pacchetto-per-
// pacchetto (Niimbot). Più lento di writeWithoutResponse ma garantisce che
// il firmware abbia ricevuto e processato ogni frame prima del successivo.
async function nativeWriteWithResponse(deviceId: string, data: Uint8Array): Promise<void> {
  const conn = nativeConns.get(deviceId);
  if (!conn) throw new Error('ble_not_connected');
  for (let off = 0; off < data.length; off += CHUNK_SIZE) {
    const chunk = data.subarray(off, off + CHUNK_SIZE);
    await BleClient.write(
      conn.deviceId, conn.serviceUuid, conn.writeCharUuid,
      new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength),
    );
  }
}

export async function bleWriteWithResponse(deviceId: string, data: Uint8Array): Promise<void> {
  if (Capacitor.isNativePlatform()) return nativeWriteWithResponse(deviceId, data);
  // Web: dati che richiedono response → writeValue (con response) sulla char.
  const conn = webConns.get(deviceId);
  if (!conn) throw new Error('ble_not_connected');
  for (let off = 0; off < data.length; off += CHUNK_SIZE) {
    const chunk = data.subarray(off, off + CHUNK_SIZE);
    // Forziamo la copia in un ArrayBuffer (non SharedArrayBuffer) per BufferSource.
    const buf = new Uint8Array(chunk.length);
    buf.set(chunk);
    await conn.char.writeValue(buf.buffer);
  }
}

// T-082: subscribe alle notification della characteristic NOTIFY (es. FF01
// su Niimbot). Niimbot risponde a ogni comando con un pacchetto di stato
// che viene messo qui nel callback. Il driver Niimbot accumula le response
// in una coda interna che viene poi consumata da `awaitReply()`.
const NIIMBOT_NOTIFY_CHAR_FF00 = '0000ff01-0000-1000-8000-00805f9b34fb';
const NIIMBOT_NOTIFY_CHAR_NUS = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';

function resolveNotifyChar(conn: NativeConn): string {
  // T-082: preferiamo la notify char detected dinamicamente in connect().
  // Fallback ai constanti hardcoded solo se il service è uno noto.
  if (conn.notifyCharUuid) return conn.notifyCharUuid;
  if (conn.serviceUuid === SVC_NUS) return NIIMBOT_NOTIFY_CHAR_NUS;
  return NIIMBOT_NOTIFY_CHAR_FF00;
}

export async function bleStartNotifications(
  deviceId: string,
  onPacket: (data: Uint8Array) => void,
): Promise<void> {
  const conn = nativeConns.get(deviceId);
  if (!conn) throw new Error('ble_not_connected');
  const notifyCharUuid = resolveNotifyChar(conn);
  if (Capacitor.isNativePlatform()) {
    await BleClient.startNotifications(
      conn.deviceId, conn.serviceUuid, notifyCharUuid,
      (value: DataView) => {
        const u8 = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
        onPacket(u8);
      },
    );
  }
}

export async function bleStopNotifications(deviceId: string): Promise<void> {
  const conn = nativeConns.get(deviceId);
  if (!conn) return;
  const notifyCharUuid = resolveNotifyChar(conn);
  if (Capacitor.isNativePlatform()) {
    try {
      await BleClient.stopNotifications(conn.deviceId, conn.serviceUuid, notifyCharUuid);
    } catch { /* ignora se già stopped */ }
  }
}

// ─────────────────────────────────────────────
// Web Bluetooth implementation
// ─────────────────────────────────────────────

async function webPick(): Promise<BlePrinterDevice[]> {
  if (!navigator.bluetooth) {
    // T-219: su iOS nessun browser espone Web Bluetooth (WebKit) — il messaggio
    // deve dire all'utente DOVE la funzione esiste, non solo che manca.
    throw new Error(
      "Questo browser non supporta il Bluetooth (Web Bluetooth). Su iPhone/iPad usa l'app " +
      'TrackEAT installata; dal computer o da Android usa Chrome o Edge.',
    );
  }
  const device = await navigator.bluetooth.requestDevice({
    filters: [
      { services: [SVC_FF00] },
      { services: [SVC_NUS] },
      { namePrefix: 'Clabel' },
      { namePrefix: 'C' },
      { namePrefix: 'XP' },
    ],
    optionalServices: [SVC_FF00, SVC_NUS, SVC_BM70],
  });
  webDeviceCache.set(device.id, device);
  return [{ deviceId: device.id, name: device.name ?? device.id, rssi: null }];
}

async function webConnect(deviceId: string): Promise<void> {
  const existing = webConns.get(deviceId);
  if (existing && existing.device.gatt?.connected) return;
  // Connessione persa o mai aperta: ricostruisci.
  if (existing) webConns.delete(deviceId);

  let device = webDeviceCache.get(deviceId);
  if (!device) {
    // Chrome supporta navigator.bluetooth.getDevices() per riprendere device
    // già "permessi" dall'utente in sessioni precedenti. Se è disponibile e
    // l'utente l'ha già accoppiato dal picker, possiamo riconnettere senza
    // rifare la scansione.
    try {
      const known = await (navigator.bluetooth as unknown as { getDevices?: () => Promise<BluetoothDevice[]> }).getDevices?.();
      const match = known?.find((d) => d.id === deviceId);
      if (match) {
        webDeviceCache.set(match.id, match);
        device = match;
      }
    } catch { /* getDevices non supportato o non permesso */ }
  }
  if (!device) throw new Error('ble_device_not_found — apri Impostazioni → Stampante e riselezionala');

  // Auto-reconnect: quando il GATT si scollega (timeout, tab in background…)
  // rimuovo dalla mappa così il prossimo write triggera una nuova connect.
  const onDisconnect = () => {
    webConns.delete(deviceId);
    device!.removeEventListener('gattserverdisconnected', onDisconnect);
  };
  device.addEventListener('gattserverdisconnected', onDisconnect);

  const server = await device.gatt!.connect();

  // Try service profiles in order
  let char: BluetoothRemoteGATTCharacteristic | null = null;
  for (const [svcUuid, chrUuid] of [
    [SVC_FF00, CHR_FF02],
    [SVC_NUS, CHR_NUS_TX],
    [SVC_BM70, CHR_BM70_WRITE],
  ] as [string, string][]) {
    try {
      const svc = await server.getPrimaryService(svcUuid);
      char = await svc.getCharacteristic(chrUuid);
      break;
    } catch {
      // service not present, try next
    }
  }

  if (!char) {
    // Fallback: enumerate all services
    const services = await server.getPrimaryServices();
    for (const svc of services) {
      const chars = await svc.getCharacteristics();
      const writable = chars.find(
        (c) => c.properties.write || c.properties.writeWithoutResponse,
      );
      if (writable) { char = writable; break; }
    }
  }

  if (!char) throw new Error('ble_no_write_char — profilo stampante non riconosciuto');
  webConns.set(deviceId, { device, char });
}

async function webWrite(deviceId: string, data: Uint8Array): Promise<void> {
  let conn = webConns.get(deviceId);
  if (!conn || !conn.device.gatt?.connected) {
    await webConnect(deviceId);
    conn = webConns.get(deviceId);
  }
  if (!conn) throw new Error('ble_not_connected');
  const { char } = conn;
  const canResp = char.properties.write;
  for (let off = 0; off < data.length; off += CHUNK_SIZE) {
    const chunk = data.subarray(off, off + CHUNK_SIZE);
    const buf = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength) as ArrayBuffer;
    if (canResp) {
      // ACK every chunk: write-with-response blocks until the printer has the
      // bytes → zero dropped chunks → raster never shifts (the striped-label fix).
      await char.writeValue(buf);
    } else {
      // No ACK channel: writeWithoutResponse + per-chunk drain delay so the
      // printer's BLE buffer doesn't overflow on a large raster.
      await char.writeValueWithoutResponse(buf);
      await sleep(WEB_PACE_MS);
    }
  }
}
